import { useContext } from "react";

import { Context } from "@reactivated";

import { IWebPageURL, isoWebPageURL } from "../models/nominals";
import { ReactivatedContext, getReactivatedContext } from "./environment";

const enum NavContext {
    CURRENT,
    TOP,
}

type UnwrappedSiteURLs = Readonly<ReactivatedContext["urls"]>;
type SiteURLs = {
    [T in keyof UnwrappedSiteURLs]:
        | IWebPageURL
        | Exclude<UnwrappedSiteURLs[T], string>;
};

const _getPageURLFromContext = <T extends keyof SiteURLs>(
    ctx: ReactivatedContext | null,
    name: T,
): SiteURLs[T] => {
    const siteURLs: UnwrappedSiteURLs | null = ctx?.urls || null;
    if (!siteURLs) {
        return isoWebPageURL.wrap("/");
    }
    const unwrappedURL = siteURLs[name];
    const url = unwrappedURL ? isoWebPageURL.wrap(unwrappedURL) : null;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return url!;
};

export const usePageURL = <T extends keyof SiteURLs>(name: T): SiteURLs[T] => {
    const ctx = useContext(Context) || getReactivatedContext();
    return _getPageURLFromContext(ctx, name);
};

/**
 * @deprecated Use the `usePageURL` hook instead.
 */
const pageURL = <T extends keyof SiteURLs>(name: T): SiteURLs[T] => {
    const ctx = getReactivatedContext();
    return _getPageURLFromContext(ctx, name);
};

const navigateToURL = (url: IWebPageURL, context = NavContext.TOP): void => {
    const unwrappedURL = isoWebPageURL.unwrap(url);
    if (context === NavContext.TOP && window.top) {
        window.top.location.href = unwrappedURL;
        return;
    }
    window.location.href = unwrappedURL;
};

const navigateToHome = (): void => {
    const homeURL = isoWebPageURL.wrap(window.location.origin);
    navigateToURL(homeURL);
};

const navigateTo = <T extends keyof SiteURLs>(name: T): void => {
    const url = pageURL(name);
    if (url) {
        navigateToURL(url);
    } else {
        navigateToHome();
    }
};

const getHash = (elem: string): boolean => {
    const hasPDPHash =
        window.location.hash.length > 0 && window.location.hash.includes(elem);
    if (hasPDPHash) {
        return true;
    }
    return false;
};

// Export Public API
export const urls = {
    pageURL,
    navigateTo,
    navigateToHome,
    navigateToURL,
    getHash,
};
export default urls;
